import * as React from "react";
import {
  MAX_PERSONA_SCRIPT_SLOT_INDEX,
  PERSONA_SCRIPT_IMPORT_MAX_BYTES,
  PERSONA_SCRIPT_IMPORT_MAX_CHARS,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import {
  useExtractPersonaScriptImport,
  useImportPersonaScriptFromText,
  useImportPersonaScriptsFromLegacy,
} from "@/lib/queries/persona-opportunity-scripts.queries";
import { amberFailureToastMessage } from "@/lib/use-amber-mutation";

import { PersonaImportFileZone } from "./PersonaImportFileZone.js";
import { PersonaImportLegacyPane } from "./PersonaImportLegacyPane.js";
import { mergeImportText } from "./merge-import-text.js";

const IMPORT_MAX_MB = PERSONA_SCRIPT_IMPORT_MAX_BYTES / (1024 * 1024);
const IMPORT_DIALOG_HEIGHT_CLASS = "h-[min(94vh,34rem)]";
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

type ImportPane = "paste" | "legacy";

export interface PersonaImportScriptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  remainingFreeSlotCount?: number;
}

function isLegacyDoc(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/msword" || (name.endsWith(".doc") && !name.endsWith(".docx"));
}

function isAllowedImportFile(file: File): boolean {
  if (isLegacyDoc(file)) return false;
  const name = file.name.toLowerCase();
  return (
    file.type === "text/plain" ||
    name.endsWith(".txt") ||
    file.type === "application/pdf" ||
    name.endsWith(".pdf") ||
    file.type === DOCX_MIME ||
    name.endsWith(".docx")
  );
}

export function PersonaImportScriptModal({
  open,
  onOpenChange,
  setId,
  tenantId,
  remainingFreeSlotCount = MAX_PERSONA_SCRIPT_SLOT_INDEX,
}: PersonaImportScriptModalProps) {
  const { t } = useTranslation();
  const extractRunIdRef = React.useRef(0);
  const textRef = React.useRef("");
  const [pane, setPane] = React.useState<ImportPane>("paste");
  const [text, setText] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [legacySetId, setLegacySetId] = React.useState("");
  const [selectedLegacyIds, setSelectedLegacyIds] = React.useState<Set<string>>(() => new Set());
  const [error, setError] = React.useState<string | null>(null);
  const acceptedRef = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const extractImport = useExtractPersonaScriptImport();
  const importScript = useImportPersonaScriptFromText({
    onAccepted: () => {
      acceptedRef.current = true;
      setIsSubmitting(false);
      onOpenChange(false);
    },
  });
  const importLegacy = useImportPersonaScriptsFromLegacy();

  const isImporting = isSubmitting || importLegacy.isPending;
  const busy = extractImport.isPending || isImporting;
  const confirmDisabled =
    busy || (pane === "paste" ? !text.trim() : selectedLegacyIds.size === 0);

  React.useEffect(() => {
    if (!open) {
      extractRunIdRef.current += 1;
      return;
    }
    acceptedRef.current = false;
    textRef.current = "";
    setText("");
    setSelectedFile(null);
    setPane("paste");
    setLegacySetId("");
    setSelectedLegacyIds(new Set());
    setError(null);
    setIsSubmitting(false);
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  const toggleLegacyId = (scriptId: string) => {
    setSelectedLegacyIds((current) => {
      const next = new Set(current);
      if (next.has(scriptId)) {
        next.delete(scriptId);
        return next;
      }
      if (next.size >= remainingFreeSlotCount) return current;
      next.add(scriptId);
      return next;
    });
  };

  const handleLegacySetChange = React.useCallback((nextSetId: string) => {
    setLegacySetId(nextSetId);
    setSelectedLegacyIds(new Set());
  }, []);

  const handleFiles = (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    if (list.length > 1) {
      setError(
        t("personaOpportunities.scripts.import.tooManyFiles", "Upload one file at a time."),
      );
      return;
    }
    void handleFile(list[0]);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (isLegacyDoc(file)) {
      setSelectedFile(null);
      setError(
        t(
          "personaOpportunities.scripts.import.legacyDoc",
          "Legacy .doc files are not supported — convert to DOCX first.",
        ),
      );
      return;
    }
    if (!isAllowedImportFile(file)) {
      setSelectedFile(null);
      setError(
        t("personaOpportunities.scripts.import.invalidType", "Upload a PDF, DOCX, or TXT file."),
      );
      return;
    }
    if (file.size > PERSONA_SCRIPT_IMPORT_MAX_BYTES) {
      setSelectedFile(null);
      setError(
        t("personaOpportunities.scripts.import.tooLarge", {
          maxMb: IMPORT_MAX_MB,
          defaultValue: "File must be {{maxMb}}MB or smaller.",
        }),
      );
      return;
    }
    setSelectedFile(file);
    const runId = ++extractRunIdRef.current;
    try {
      const extracted = await extractImport.mutateAsync({ setId, tenantId, file });
      if (extractRunIdRef.current !== runId) return;
      const merged = mergeImportText(textRef.current, extracted.text, PERSONA_SCRIPT_IMPORT_MAX_CHARS);
      if (!merged.ok) {
        setError(
          t("personaOpportunities.scripts.import.overCap", {
            max: PERSONA_SCRIPT_IMPORT_MAX_CHARS.toLocaleString(),
            defaultValue: "Import text cannot exceed {{max}} characters.",
          }),
        );
        return;
      }
      textRef.current = merged.text;
      setText(merged.text);
    } catch (err) {
      if (extractRunIdRef.current !== runId) return;
      setError(
        getLocalUserFacingApiMessage(err) ??
          t(
            "personaOpportunities.scripts.import.extractFailed",
            "Could not read that file. Try another PDF, DOCX, or TXT.",
          ),
      );
    }
  };

  const handlePasteConfirm = () => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setError(null);
    setIsSubmitting(true);
    importScript.mutate(
      { setId, tenantId, text: trimmed },
      {
        onError: (err) => {
          setIsSubmitting(false);
          if (acceptedRef.current) return;
          setError(
            amberFailureToastMessage(err) ??
              t("personaOpportunities.scripts.import.failed", "Could not import that script. Try again."),
          );
        },
      },
    );
  };

  const handleLegacyConfirm = () => {
    if (
      !legacySetId ||
      selectedLegacyIds.size === 0 ||
      selectedLegacyIds.size > remainingFreeSlotCount ||
      busy
    ) {
      return;
    }
    setError(null);
    importLegacy.mutate(
      { setId, tenantId, legacySetId, scriptIds: [...selectedLegacyIds] },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => {
          setError(
            getLocalUserFacingApiMessage(err, passThroughMessage) ??
              t("personaOpportunities.scripts.import.failed", "Could not import that script. Try again."),
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="form"
        className={cn(
          "grid grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[680px]",
          IMPORT_DIALOG_HEIGHT_CLASS,
        )}
        data-testid="persona-import-script-modal"
      >
        <DialogHeader className="border-b border-border px-6 pt-5 pr-12 pb-4">
          <DialogTitle className="text-[15px] font-semibold">
            {t("personaOpportunities.scripts.import.title", "Import script")}
          </DialogTitle>
          <DialogDescription className="mt-1 text-[13px] leading-relaxed">
            <span className="grid">
              <span
                className={cn("col-start-1 row-start-1", pane === "legacy" && "invisible")}
                aria-hidden={pane === "legacy"}
              >
                {t(
                  "personaOpportunities.scripts.import.description",
                  "Paste a script, or upload one file. Review the text below, then confirm.",
                )}
              </span>
              <span
                className={cn("col-start-1 row-start-1", pane !== "legacy" && "invisible")}
                aria-hidden={pane !== "legacy"}
              >
                {t(
                  "personaOpportunities.scripts.import.legacyDescription",
                  "Pick scripts from your Opportunities sets. Confirm copies them onto Your Scripts.",
                )}
              </span>
            </span>
          </DialogDescription>
          <Tabs
            value={pane}
            onValueChange={(value) => setPane(value as ImportPane)}
            className="mt-3 gap-0"
          >
            <TabsList variant="line">
              <TabsTrigger value="paste" disabled={busy} data-testid="persona-import-tab-paste">
                {t("personaOpportunities.scripts.import.tabPaste", "Paste or file")}
              </TabsTrigger>
              <TabsTrigger value="legacy" disabled={busy} data-testid="persona-import-tab-legacy">
                {t("personaOpportunities.scripts.import.tabLegacy", "From Opportunities")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </DialogHeader>

        <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto] overflow-hidden">
          <div className="relative min-h-0">
            <div
              className={cn(
                "absolute inset-0 flex flex-col overflow-hidden px-6 py-4",
                pane !== "paste" && "hidden",
              )}
            >
              <PersonaImportFileZone
                file={selectedFile}
                isExtracting={extractImport.isPending}
                disabled={busy}
                onFiles={handleFiles}
              />
              {selectedFile ? (
                <p className="mt-3 mb-1.5 shrink-0 text-xs text-muted-foreground">
                  {t(
                    "personaOpportunities.scripts.import.reviewHint",
                    "Review and edit before confirming",
                  )}
                </p>
              ) : (
                <div className="flex items-center gap-3 py-3">
                  <span className="h-px min-w-0 flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">
                    {t("personaOpportunities.scripts.import.orPaste", "or paste")}
                  </span>
                  <span className="h-px min-w-0 flex-1 bg-border" />
                </div>
              )}
              <label htmlFor="persona-import-script-text" className="sr-only">
                {t("personaOpportunities.scripts.import.scriptLabel", "Script")}
              </label>
              <Textarea
                id="persona-import-script-text"
                data-testid="persona-import-script-text"
                value={text}
                maxLength={PERSONA_SCRIPT_IMPORT_MAX_CHARS}
                aria-label={t("personaOpportunities.scripts.import.placeholder", "Paste your script…")}
                onChange={(event) => {
                  textRef.current = event.target.value;
                  setText(event.target.value);
                }}
                disabled={busy}
                placeholder={t("personaOpportunities.scripts.import.placeholder", "Paste your script…")}
                className="min-h-0 flex-1 resize-none text-[13px] leading-relaxed"
              />
              <p className="mt-1.5 shrink-0 text-xs text-muted-foreground" aria-live="polite">
                {t("personaOpportunities.scripts.import.charCount", {
                  count: text.length,
                  max: PERSONA_SCRIPT_IMPORT_MAX_CHARS.toLocaleString(),
                  defaultValue: "{{count}} / {{max}} characters",
                })}
              </p>
            </div>
            <div
              className={cn(
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden px-6 py-4",
                pane !== "legacy" && "hidden",
              )}
            >
              <PersonaImportLegacyPane
                open={open}
                tenantId={tenantId}
                disabled={busy}
                selectedIds={selectedLegacyIds}
                maxSelectable={remainingFreeSlotCount}
                onSetChange={handleLegacySetChange}
                onToggleId={toggleLegacyId}
                onUsePaste={() => setPane("paste")}
              />
            </div>
          </div>
          {isImporting || error ? (
            <div className="px-6 pb-3">
              {isImporting ? (
                <p role="status" className="text-xs text-muted-foreground">
                  {pane === "legacy"
                    ? t("personaOpportunities.scripts.import.legacyImporting", "Importing…")
                    : t("personaOpportunities.scripts.import.importing", "Importing…")}
                </p>
              ) : null}
              {error ? (
                <p role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter className="!mx-0 !mb-0 flex flex-wrap items-center justify-between gap-4 !bg-muted !px-6 py-3 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="h-[42px] px-3.5"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            data-testid="persona-import-confirm"
            className={cn(
              "h-[42px] px-5 font-semibold",
              isImporting && "[&_svg]:motion-safe:animate-spin [&_svg]:motion-reduce:animate-none",
            )}
            disabled={confirmDisabled}
            iconStart={isImporting ? "loader" : undefined}
            onClick={pane === "legacy" ? handleLegacyConfirm : handlePasteConfirm}
          >
            {isImporting
              ? pane === "legacy"
                ? t("personaOpportunities.scripts.import.legacyImporting", "Importing…")
                : t("personaOpportunities.scripts.import.importing", "Importing…")
              : pane === "legacy"
                ? selectedLegacyIds.size > 0
                  ? t("personaOpportunities.scripts.import.legacyConfirm", {
                      count: selectedLegacyIds.size,
                      defaultValue_one: "Confirm {{count}} script",
                      defaultValue_other: "Confirm {{count}} scripts",
                    })
                  : t("personaOpportunities.scripts.import.confirm", "Confirm")
                : t("personaOpportunities.scripts.import.confirm", "Confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
